import React, { useState, useEffect, useRef } from 'react';
import { TextStyle, ContainerStyle, LayoutStyle, Screen, LayoutType, PriceBoxStyle } from '../../../shared/types';
import { processImage, ImageProcessInfo } from '../utils/imageCompression';
import { clonePanfletoContentDeep } from '../../banners/utils/panfletoContentUtils';

interface StyleEditorProps {
  screen: Screen;
  onStyleChange: (layoutStyle: LayoutStyle) => void;
  onGetCurrentStyle?: (getStyle: () => LayoutStyle) => void;
  forceSyncKey?: number; // Key para forçar sincronização quando importar
  numeroColunas?: number; // Número de colunas (1, 2 ou 3) - apenas para simple2col
}

// Default styles for each layout type
const getDefaultStyles = (layoutType: LayoutType): LayoutStyle => {
  const baseTextStyle: TextStyle = {
    color: '#000000',
    fontSize: 16,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 400,
    verticalStretch: 100,
    showCurrency: true,
  };

  const baseContainerStyle: ContainerStyle = {
    backgroundColor: '#ffffff',
    useGradient: false,
    gradientStart: '#ffffff',
    gradientEnd: '#f0f0f0',
    borderRadius: 8,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    paddingRight: 16,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    horizontalGap: 8,
    verticalGap: 8,
    backgroundVignetteEnabled: false,
    backgroundVignetteIntensity: 35,
  };

  const basePriceBoxStyle: PriceBoxStyle = {
    enabled: false,
    backgroundColor: '#ffffff',
    borderColor: '#000000',
    borderWidth: 0,
    borderRadius: 8,
    paddingTop: 4,
    paddingRight: 8,
    paddingBottom: 4,
    paddingLeft: 8,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
  };

  switch (layoutType) {
    case 'simple2col':
      return {
        productName: { ...baseTextStyle, fontSize: 24, color: '#333333' },
        price: { ...baseTextStyle, fontSize: 32, color: '#006400' },
        unit: { ...baseTextStyle, fontSize: 18, color: '#666666' },
        description: { ...baseTextStyle, fontSize: 16, color: '#666666' },
        category: { ...baseTextStyle, fontSize: 14, color: '#666666' },
        validUntil: { ...baseTextStyle, fontSize: 14, color: '#cc0000' },
        productContainer: { ...baseContainerStyle },
        priceBox: { ...basePriceBoxStyle },
      };
    case 'panfleto':
      return {
        productName: { ...baseTextStyle, fontSize: 48, color: '#333333' },
        price: { ...baseTextStyle, fontSize: 60, color: '#cc0000' },
        unit: { ...baseTextStyle, fontSize: 24, color: '#666666' },
        description: { ...baseTextStyle, fontSize: 24, color: '#666666' },
        category: { ...baseTextStyle, fontSize: 24, color: '#666666' },
        validUntil: { ...baseTextStyle, fontSize: 20, color: '#cc0000' },
        productContainer: { ...baseContainerStyle },
        priceBox: { ...basePriceBoxStyle },
      };
    case 'video':
      return {
        productName: { ...baseTextStyle, fontSize: 24, color: '#333333' },
        price: { ...baseTextStyle, fontSize: 32, color: '#006400' },
        unit: { ...baseTextStyle, fontSize: 18, color: '#666666' },
        description: { ...baseTextStyle, fontSize: 16, color: '#666666' },
        category: { ...baseTextStyle, fontSize: 14, color: '#666666' },
        validUntil: { ...baseTextStyle, fontSize: 14, color: '#cc0000' },
        productContainer: { ...baseContainerStyle },
        priceBox: { ...basePriceBoxStyle },
      };
    default:
      return {
        productName: { ...baseTextStyle },
        price: { ...baseTextStyle },
        unit: { ...baseTextStyle },
        productContainer: { ...baseContainerStyle },
        priceBox: { ...basePriceBoxStyle },
      };
  }
};

const StyleEditor: React.FC<StyleEditorProps> = ({ screen, onStyleChange, onGetCurrentStyle, forceSyncKey, numeroColunas }) => {
  // Debug: verificar numeroColunas
  if (process.env.NODE_ENV === 'development') {
    console.log('StyleEditor - numeroColunas:', numeroColunas, 'screen.layoutId:', screen.layoutId);
  }
  
  // Use the screen's existing style or create a default one based on layout type
  const [layoutStyle, setLayoutStyle] = useState<LayoutStyle>(
    screen.layoutStyle || getDefaultStyles(screen.layoutId)
  );
  const [isCompressingImage, setIsCompressingImage] = useState(false);
  const [imageInfo, setImageInfo] = useState<ImageProcessInfo | null>(null);
  
  // IMPORTANTE: Salvar valores anteriores de marginRight e marginLeft quando desabilitar (3 colunas)
  // para restaurar quando voltar para 1 ou 2 colunas
  const savedMarginsRef = useRef<{ marginRight: number; marginLeft: number } | null>(null);

  // Função helper para criar cópia profunda SEGURA que preserva TODOS os dados
  const deepCopyLayoutStyle = (style: LayoutStyle): LayoutStyle => {
    // Usar spread operator e cópia manual para garantir que NADA seja perdido
    // CRÍTICO: Preservar TODOS os dados, especialmente imagens base64
    return {
      productName: style.productName ? { ...style.productName } : undefined,
      price: style.price ? { ...style.price } : undefined,
      unit: style.unit ? { ...style.unit } : undefined,
      productContainer: style.productContainer ? {
        ...style.productContainer,
        // CRÍTICO: Preservar backgroundImage exatamente como está (pode ser base64 muito grande)
        backgroundImage: style.productContainer.backgroundImage,
      } : undefined,
      description: style.description ? { ...style.description } : undefined,
      category: style.category ? { ...style.category } : undefined,
      validUntil: style.validUntil ? { ...style.validUntil } : undefined,
      priceBox: style.priceBox ? { ...style.priceBox } : undefined,
      panfletoContent: style.panfletoContent ? clonePanfletoContentDeep(style.panfletoContent) : undefined,
    } as LayoutStyle;
  };

  // Sincronizar quando a tela mudar (ex: quando carregar da API) ou quando forceSyncKey mudar (importação)
  // IMPORTANTE: NÃO sincronizar quando screen.layoutStyle muda devido a edições locais
  // Apenas sincronizar quando a tela muda (screen.id) ou quando força sincronização (forceSyncKey)
  useEffect(() => {
    // Limpar informações de imagem quando trocar de tela
    setImageInfo(null);
    // Se houver layoutStyle na tela, usar ele
    // IMPORTANTE: Criar uma cópia profunda SEGURA para garantir que o estado interno seja atualizado
    // CRÍTICO: Preservar TODOS os dados, especialmente imagens base64
    if (screen.layoutStyle) {
      const newLayoutStyle = deepCopyLayoutStyle(screen.layoutStyle);
      setLayoutStyle(newLayoutStyle);
      // Notificar mudança para atualizar preview
      onStyleChange(newLayoutStyle);
    } else {
      // Se não houver layoutStyle, usar default baseado no layoutId
      const defaultStyle = getDefaultStyles(screen.layoutId);
      setLayoutStyle(defaultStyle);
      // Notificar mudança para atualizar preview
      onStyleChange(defaultStyle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen.layoutId, screen.id, forceSyncKey]); // Removido screen.layoutStyle para evitar loop durante edições

  // IMPORTANTE: Gerenciar marginRight e marginLeft baseado em numeroColunas
  // Quando for 3 colunas: salvar valores anteriores e forçar 0
  // Quando voltar para 1 ou 2 colunas: restaurar valores anteriores
  useEffect(() => {
    if (!layoutStyle.productContainer) return;
    
    const currentMarginRight = layoutStyle.productContainer.marginRight ?? 0;
    const currentMarginLeft = layoutStyle.productContainer.marginLeft ?? 0;
    
    if (numeroColunas === 3) {
      // Se mudou para 3 colunas e ainda não salvou os valores anteriores, salvar
      if (savedMarginsRef.current === null) {
        savedMarginsRef.current = {
          marginRight: currentMarginRight,
          marginLeft: currentMarginLeft
        };
      }
      
      // Forçar valores para 0 na visualização
      if (currentMarginRight !== 0 || currentMarginLeft !== 0) {
        const newLayoutStyle = {
          ...layoutStyle,
          productContainer: {
            ...layoutStyle.productContainer,
            marginRight: 0,
            marginLeft: 0
          }
        };
        setLayoutStyle(newLayoutStyle);
        onStyleChange(newLayoutStyle);
      }
    } else if (numeroColunas === 1 || numeroColunas === 2) {
      // Se voltou para 1 ou 2 colunas e há valores salvos, restaurar
      if (savedMarginsRef.current !== null) {
        const { marginRight, marginLeft } = savedMarginsRef.current;
        // Só restaurar se os valores atuais forem 0 (ou seja, foram zerados quando estava em 3 colunas)
        if (currentMarginRight === 0 && currentMarginLeft === 0) {
          const newLayoutStyle = {
            ...layoutStyle,
            productContainer: {
              ...layoutStyle.productContainer,
              marginRight: marginRight,
              marginLeft: marginLeft
            }
          };
          setLayoutStyle(newLayoutStyle);
          onStyleChange(newLayoutStyle);
        }
        // Limpar valores salvos após restaurar
        savedMarginsRef.current = null;
      }
    }
  }, [numeroColunas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expor função para obter o estado atual (para exportação)
  // IMPORTANTE: Usar useRef para evitar re-renders desnecessários durante arraste de sliders
  const layoutStyleRef = React.useRef(layoutStyle);
  layoutStyleRef.current = layoutStyle;

  useEffect(() => {
    if (onGetCurrentStyle) {
      // Usar ref para obter sempre o valor mais atual sem causar re-render
      onGetCurrentStyle(() => layoutStyleRef.current);
    }
  }, [onGetCurrentStyle]);

  // Handler for text style changes
  const handleTextStyleChange = (
    element: keyof Omit<LayoutStyle, 'productContainer' | 'priceBox' | 'panfletoContent'>,
    property: keyof TextStyle,
    value: string | number | boolean
  ) => {
    const newLayoutStyle = { ...layoutStyle };

    // Get current element style or create a new one with default values
    const currentElementStyle = newLayoutStyle[element] || { color: '#000000', fontSize: 16 };

    // Update the specific property
    newLayoutStyle[element] = {
      ...currentElementStyle,
      [property]: value,
    };

    setLayoutStyle(newLayoutStyle);
    onStyleChange(newLayoutStyle);
  };

  // Handler for container style changes
  const handleContainerStyleChange = (
    property: keyof ContainerStyle,
    value: string | number | boolean | undefined
  ) => {
    // IMPORTANTE: Quando for 3 colunas, forçar marginRight e marginLeft para 0
    const finalValue = (numeroColunas === 3 && (property === 'marginRight' || property === 'marginLeft')) 
      ? 0 
      : value;
    
    const newLayoutStyle = {
      ...layoutStyle,
      productContainer: {
        ...layoutStyle.productContainer,
        [property]: finalValue,
        // Garantir que quando for 3 colunas, marginRight e marginLeft sempre sejam 0
        ...(numeroColunas === 3 ? {
          marginRight: 0,
          marginLeft: 0
        } : {})
      },
    };
    setLayoutStyle(newLayoutStyle);
    onStyleChange(newLayoutStyle);
  };

  // Handler for price box style changes
  const handlePriceBoxStyleChange = (
    property: keyof PriceBoxStyle,
    value: string | number | boolean | undefined
  ) => {
    const current = layoutStyle.priceBox || {
      enabled: false,
      backgroundColor: '#ffffff',
      borderColor: '#000000',
      borderWidth: 0,
      borderRadius: 8,
      paddingTop: 4,
      paddingRight: 8,
      paddingBottom: 4,
      paddingLeft: 8,
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,
    };
    const newLayoutStyle = {
      ...layoutStyle,
      priceBox: {
        ...current,
        [property]: value,
      },
    };
    setLayoutStyle(newLayoutStyle);
    onStyleChange(newLayoutStyle);
  };
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold mb-4">Estilo do Layout</h3>

      <div className="space-y-6">
        {/* Product Container Styling */}
        <div className="border-b pb-4">
          <h4 className="font-medium mb-3">Container do Produto</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Background Color / Gradient */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Fundo</label>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={layoutStyle.productContainer.useGradient}
                  onChange={(e) =>
                    handleContainerStyleChange('useGradient', e.target.checked)
                  }
                  className="w-4 h-4"
                />
                <span className="text-sm">Usar gradiente</span>
              </div>

              {layoutStyle.productContainer.useGradient ? (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Cor Inicial</label>
                    <input
                      type="color"
                      value={layoutStyle.productContainer.gradientStart || '#ffffff'}
                      onChange={(e) =>
                        handleContainerStyleChange('gradientStart', e.target.value)
                      }
                      className="w-full h-8"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Cor Final</label>
                    <input
                      type="color"
                      value={layoutStyle.productContainer.gradientEnd || '#f0f0f0'}
                      onChange={(e) =>
                        handleContainerStyleChange('gradientEnd', e.target.value)
                      }
                      className="w-full h-8"
                    />
                  </div>
                </div>
              ) : (
                <div className="mt-2">
                  <label className="block text-xs text-gray-500 mb-1">Cor do Fundo</label>
                  <input
                    type="color"
                    value={layoutStyle.productContainer.backgroundColor || '#ffffff'}
                    onChange={(e) =>
                      handleContainerStyleChange('backgroundColor', e.target.value)
                    }
                    className="w-full h-8"
                  />
                </div>
              )}
            </div>

            {/* Border Radius */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Borda Arredondada</label>
              <input
                type="range"
                min="0"
                max="32"
                value={layoutStyle.productContainer.borderRadius || 0}
                onChange={(e) =>
                  handleContainerStyleChange('borderRadius', parseInt(e.target.value))
                }
                className="w-full"
              />
              <span className="text-xs text-gray-500 block mt-1">
                {layoutStyle.productContainer.borderRadius}px
              </span>
            </div>
          </div>

          {/* Padding Controls */}
          <div className="mt-4">
            <label className="block text-sm text-gray-600 mb-1">Espaçamento Interno</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Superior</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.paddingTop}
                  onChange={(e) =>
                    handleContainerStyleChange('paddingTop', parseInt(e.target.value))
                  }
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Direita</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.paddingRight}
                  onChange={(e) =>
                    handleContainerStyleChange('paddingRight', parseInt(e.target.value))
                  }
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Inferior</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.paddingBottom}
                  onChange={(e) =>
                    handleContainerStyleChange('paddingBottom', parseInt(e.target.value))
                  }
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Esquerda</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.paddingLeft}
                  onChange={(e) =>
                    handleContainerStyleChange('paddingLeft', parseInt(e.target.value))
                  }
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
            </div>
          </div>

          {/* Margin Controls */}
          <div className="mt-4">
            <label className="block text-sm text-gray-600 mb-1">Espaçamento Externo</label>
            {numeroColunas === 3 && (
              <p className="text-xs text-amber-600 mb-2 bg-amber-50 p-2 rounded border border-amber-200">
                <strong>Nota:</strong> Para layouts com 3 colunas, o espaçamento esquerdo e direito não podem ser alterados. Para mudar a quantidade de colunas, vá na página de visualização da tela selecionada.
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Superior</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.marginTop ?? 0}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    handleContainerStyleChange('marginTop', value);
                  }}
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Direita</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.marginRight ?? 0}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    handleContainerStyleChange('marginRight', value);
                  }}
                  disabled={numeroColunas === 3}
                  className={`w-full p-1 border border-gray-300 rounded ${numeroColunas === 3 ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Inferior</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.marginBottom ?? 0}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    handleContainerStyleChange('marginBottom', value);
                  }}
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Esquerda</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.marginLeft ?? 0}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    handleContainerStyleChange('marginLeft', value);
                  }}
                  disabled={numeroColunas === 3}
                  className={`w-full p-1 border border-gray-300 rounded ${numeroColunas === 3 ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
                />
              </div>
            </div>
          </div>

          {/* Price Container Styling */}
          <div className="mt-4 border-t pt-4">
            <h4 className="font-medium mb-3">Container do Preço</h4>

            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={layoutStyle.priceBox?.enabled === true}
                onChange={(e) => handlePriceBoxStyleChange('enabled', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">Ativar destaque do preço</span>
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${layoutStyle.priceBox?.enabled === false ? 'opacity-60' : ''}`}>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Cor do Fundo</label>
                <input
                  type="color"
                  value={layoutStyle.priceBox?.backgroundColor || '#ffffff'}
                  onChange={(e) => handlePriceBoxStyleChange('backgroundColor', e.target.value)}
                  className="w-full h-8"
                  disabled={layoutStyle.priceBox?.enabled === false}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Cor da Borda</label>
                <input
                  type="color"
                  value={layoutStyle.priceBox?.borderColor || '#000000'}
                  onChange={(e) => handlePriceBoxStyleChange('borderColor', e.target.value)}
                  className="w-full h-8"
                  disabled={layoutStyle.priceBox?.enabled === false}
                />
              </div>
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 ${layoutStyle.priceBox?.enabled === false ? 'opacity-60' : ''}`}>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Largura da Borda</label>
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={layoutStyle.priceBox?.borderWidth ?? 0}
                  onChange={(e) => handlePriceBoxStyleChange('borderWidth', parseInt(e.target.value || '0'))}
                  className="w-full p-1 border border-gray-300 rounded"
                  disabled={layoutStyle.priceBox?.enabled === false}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Borda Arredondada</label>
                <input
                  type="range"
                  min="0"
                  max="32"
                  value={layoutStyle.priceBox?.borderRadius ?? 8}
                  onChange={(e) => handlePriceBoxStyleChange('borderRadius', parseInt(e.target.value))}
                  className="w-full"
                  disabled={layoutStyle.priceBox?.enabled === false}
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.priceBox?.borderRadius ?? 8}px
                </span>
              </div>
            </div>

            <div className={`mt-4 ${layoutStyle.priceBox?.enabled === false ? 'opacity-60' : ''}`}>
              <label className="block text-sm text-gray-600 mb-1">Espaçamento Interno</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Superior</label>
                  <input
                    type="number"
                    min="0"
                    max="64"
                    value={layoutStyle.priceBox?.paddingTop ?? 4}
                    onChange={(e) => handlePriceBoxStyleChange('paddingTop', parseInt(e.target.value || '0'))}
                    className="w-full p-1 border border-gray-300 rounded"
                    disabled={layoutStyle.priceBox?.enabled === false}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Direita</label>
                  <input
                    type="number"
                    min="0"
                    max="64"
                    value={layoutStyle.priceBox?.paddingRight ?? 8}
                    onChange={(e) => handlePriceBoxStyleChange('paddingRight', parseInt(e.target.value || '0'))}
                    className="w-full p-1 border border-gray-300 rounded"
                    disabled={layoutStyle.priceBox?.enabled === false}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Inferior</label>
                  <input
                    type="number"
                    min="0"
                    max="64"
                    value={layoutStyle.priceBox?.paddingBottom ?? 4}
                    onChange={(e) => handlePriceBoxStyleChange('paddingBottom', parseInt(e.target.value || '0'))}
                    className="w-full p-1 border border-gray-300 rounded"
                    disabled={layoutStyle.priceBox?.enabled === false}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Esquerda</label>
                  <input
                    type="number"
                    min="0"
                    max="64"
                    value={layoutStyle.priceBox?.paddingLeft ?? 8}
                    onChange={(e) => handlePriceBoxStyleChange('paddingLeft', parseInt(e.target.value || '0'))}
                    className="w-full p-1 border border-gray-300 rounded"
                    disabled={layoutStyle.priceBox?.enabled === false}
                  />
                </div>
              </div>
            </div>

            <div className={`mt-4 ${layoutStyle.priceBox?.enabled === false ? 'opacity-60' : ''}`}>
              <label className="block text-sm text-gray-600 mb-1">Espaçamento Externo</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Superior</label>
                  <input
                    type="number"
                    min="0"
                    max="64"
                    value={layoutStyle.priceBox?.marginTop ?? 0}
                    onChange={(e) => handlePriceBoxStyleChange('marginTop', parseInt(e.target.value || '0'))}
                    className="w-full p-1 border border-gray-300 rounded"
                    disabled={layoutStyle.priceBox?.enabled === false}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Direita</label>
                  <input
                    type="number"
                    min="0"
                    max="64"
                    value={layoutStyle.priceBox?.marginRight ?? 0}
                    onChange={(e) => handlePriceBoxStyleChange('marginRight', parseInt(e.target.value || '0'))}
                    className="w-full p-1 border border-gray-300 rounded"
                    disabled={layoutStyle.priceBox?.enabled === false}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Inferior</label>
                  <input
                    type="number"
                    min="0"
                    max="64"
                    value={layoutStyle.priceBox?.marginBottom ?? 0}
                    onChange={(e) => handlePriceBoxStyleChange('marginBottom', parseInt(e.target.value || '0'))}
                    className="w-full p-1 border border-gray-300 rounded"
                    disabled={layoutStyle.priceBox?.enabled === false}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Esquerda</label>
                  <input
                    type="number"
                    min="0"
                    max="64"
                    value={layoutStyle.priceBox?.marginLeft ?? 0}
                    onChange={(e) => handlePriceBoxStyleChange('marginLeft', parseInt(e.target.value || '0'))}
                    className="w-full p-1 border border-gray-300 rounded"
                    disabled={layoutStyle.priceBox?.enabled === false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Background Image Controls */}
          <div className="mt-4 border-t pt-4">
            <h5 className="font-medium text-sm mb-3">Imagem de Fundo</h5>

            {/* Upload Image */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Imagem de Fundo</label>
              <input
                type="file"
                accept="image/*"
                disabled={isCompressingImage}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setIsCompressingImage(true);
                    try {
                      // Processa a imagem: comprime se necessário (>1MB) e converte para base64
                      const imageProcessInfo = await processImage(file);
                      setImageInfo(imageProcessInfo);
                      // Quando uma imagem é inserida, automaticamente habilitar a exibição e os sliders
                      const newLayoutStyle = {
                        ...layoutStyle,
                        productContainer: {
                          ...layoutStyle.productContainer,
                          backgroundImage: imageProcessInfo.base64,
                          backgroundImageVisible: true, // Habilitar automaticamente
                        },
                      };
                      setLayoutStyle(newLayoutStyle);
                      onStyleChange(newLayoutStyle);
                    } catch (error) {
                      console.error('Erro ao processar imagem:', error);
                      alert('Erro ao processar a imagem. Por favor, tente novamente.');
                      setImageInfo(null);
                    } finally {
                      setIsCompressingImage(false);
                    }
                  }
                }}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {isCompressingImage && (
                <p className="mt-2 text-xs text-blue-600">
                  Carregando imagem... Por favor, aguarde.
                </p>
              )}
              {layoutStyle.productContainer.backgroundImage && (
                <div className="mt-2 relative">
                  <img
                    src={layoutStyle.productContainer.backgroundImage}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded border border-gray-300"
                  />
                  <button
                    onClick={() => {
                      handleContainerStyleChange('backgroundImage', undefined);
                      setImageInfo(null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                  >
                    Remover
                  </button>
                </div>
              )}
            </div>

            {/* Image Visibility Toggle */}
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={layoutStyle.productContainer.backgroundImageVisible !== false}
                  onChange={(e) =>
                    handleContainerStyleChange('backgroundImageVisible', e.target.checked)
                  }
                  className="w-4 h-4"
                  disabled={!layoutStyle.productContainer.backgroundImage}
                />
                <span className="text-sm text-gray-600">Exibir imagem de fundo</span>
              </div>
            </div>

            {/* Image Opacity */}
            {layoutStyle.productContainer.backgroundImage && (
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">Opacidade do fundo do produto</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={layoutStyle.productContainer.backgroundImageOpacity ?? 100}
                  onChange={(e) =>
                    handleContainerStyleChange('backgroundImageOpacity', parseInt(e.target.value))
                  }
                  className="w-full"
                  disabled={layoutStyle.productContainer.backgroundImageVisible === false}
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.productContainer.backgroundImageOpacity ?? 100}%
                </span>
              </div>
            )}

            {/* Image Blur */}
            {layoutStyle.productContainer.backgroundImage && (
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">Desfoque (Blur)</label>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={layoutStyle.productContainer.backgroundImageBlur ?? 0}
                  onChange={(e) =>
                    handleContainerStyleChange('backgroundImageBlur', parseInt(e.target.value))
                  }
                  className="w-full"
                  disabled={layoutStyle.productContainer.backgroundImageVisible === false}
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.productContainer.backgroundImageBlur ?? 0}px
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Text Styling */}
        <div>
          <h4 className="font-medium mb-3">Estilos de Texto</h4>

          {/* Product Name */}
          <div className="mb-4 pb-3 border-b">
            <div className="flex justify-between items-center mb-2">
              <label className="font-medium text-sm">Nome do Produto</label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cor</label>
                <input
                  type="color"
                  value={layoutStyle.productName?.color || '#000000'}
                  onChange={(e) =>
                    handleTextStyleChange('productName', 'color', e.target.value)
                  }
                  className="w-full h-8"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tamanho (px)</label>
                <input
                  type="number"
                  min="0"
                  max="120"
                  value={layoutStyle.productName?.fontSize === 0 ? '' : (layoutStyle.productName?.fontSize || '')}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                    handleTextStyleChange('productName', 'fontSize', isNaN(value) ? 0 : value);
                  }}
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Fonte</label>
                <select
                  value={layoutStyle.productName?.fontFamily || 'Arial, sans-serif'}
                  onChange={(e) =>
                    handleTextStyleChange('productName', 'fontFamily', e.target.value)
                  }
                  className="w-full p-1 border border-gray-300 rounded"
                >
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="'Courier New', monospace">Courier New</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                  <option value="'Segoe UI', sans-serif">Segoe UI</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Altura da Linha</label>
                <input
                  type="range"
                  min="0.8"
                  max="3"
                  step="0.1"
                  value={layoutStyle.productName?.lineHeight || 1.2}
                  onChange={(e) =>
                    handleTextStyleChange('productName', 'lineHeight', parseFloat(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.productName?.lineHeight || 1.2}
                </span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Espaçamento entre Letras (px)</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  value={layoutStyle.productName?.letterSpacing || 0}
                  onChange={(e) =>
                    handleTextStyleChange('productName', 'letterSpacing', parseFloat(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.productName?.letterSpacing || 0}px
                </span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Espessura da Fonte</label>
                <input
                  type="range"
                  min="100"
                  max="900"
                  step="100"
                  value={layoutStyle.productName?.fontWeight || 400}
                  onChange={(e) =>
                    handleTextStyleChange('productName', 'fontWeight', parseInt(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.productName?.fontWeight || 400}
                </span>
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">Esticar Verticalmente (%)</label>
              <input
                type="range"
                min="100"
                max="300"
                step="10"
                value={layoutStyle.productName?.verticalStretch || 100}
                onChange={(e) =>
                  handleTextStyleChange('productName', 'verticalStretch', parseInt(e.target.value))
                }
                className="w-full"
              />
              <span className="text-xs text-gray-500 block mt-1">
                {layoutStyle.productName?.verticalStretch || 100}%
              </span>
            </div>

            <div className="mt-3 p-2 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-600">
                <strong>Nota:</strong> No layout de 2 colunas, o nome do produto não quebra linha automaticamente e será exibido em uma única linha com reticências (...) se for muito longo. Ajuste o tamanho do nome conforme necessário.
              </p>
            </div>
          </div>

          {/* Price */}
          <div className="mb-4 pb-3 border-b">
            <div className="flex justify-between items-center mb-2">
              <label className="font-medium text-sm">Preço</label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cor</label>
                <input
                  type="color"
                  value={layoutStyle.price?.color || '#000000'}
                  onChange={(e) =>
                    handleTextStyleChange('price', 'color', e.target.value)
                  }
                  className="w-full h-8"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tamanho (px)</label>
                <input
                  type="number"
                  min="0"
                  max="120"
                  value={layoutStyle.price?.fontSize === 0 ? '' : (layoutStyle.price?.fontSize || '')}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                    handleTextStyleChange('price', 'fontSize', isNaN(value) ? 0 : value);
                  }}
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Fonte</label>
                <select
                  value={layoutStyle.price?.fontFamily || 'Arial, sans-serif'}
                  onChange={(e) =>
                    handleTextStyleChange('price', 'fontFamily', e.target.value)
                  }
                  className="w-full p-1 border border-gray-300 rounded"
                >
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="'Courier New', monospace">Courier New</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                  <option value="'Segoe UI', sans-serif">Segoe UI</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Altura da Linha</label>
                <input
                  type="range"
                  min="0.8"
                  max="3"
                  step="0.1"
                  value={layoutStyle.price?.lineHeight || 1.2}
                  onChange={(e) =>
                    handleTextStyleChange('price', 'lineHeight', parseFloat(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.price?.lineHeight || 1.2}
                </span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Espaçamento entre Letras (px)</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  value={layoutStyle.price?.letterSpacing || 0}
                  onChange={(e) =>
                    handleTextStyleChange('price', 'letterSpacing', parseFloat(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.price?.letterSpacing || 0}px
                </span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Espessura da Fonte</label>
                <input
                  type="range"
                  min="100"
                  max="900"
                  step="100"
                  value={layoutStyle.price?.fontWeight || 400}
                  onChange={(e) =>
                    handleTextStyleChange('price', 'fontWeight', parseInt(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.price?.fontWeight || 400}
                </span>
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">Esticar Verticalmente (%)</label>
              <input
                type="range"
                min="100"
                max="300"
                step="10"
                value={layoutStyle.price?.verticalStretch || 100}
                onChange={(e) =>
                  handleTextStyleChange('price', 'verticalStretch', parseInt(e.target.value))
                }
                className="w-full"
              />
              <span className="text-xs text-gray-500 block mt-1">
                {layoutStyle.price?.verticalStretch || 100}%
              </span>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <input
                type="checkbox"
                id="showCurrency"
                checked={layoutStyle.price?.showCurrency !== false}
                onChange={(e) =>
                  handleTextStyleChange('price', 'showCurrency', e.target.checked)
                }
                className="w-4 h-4"
              />
              <label htmlFor="showCurrency" className="text-xs text-gray-700">
                Mostrar símbolo da moeda (R$)
              </label>
            </div>
          </div>

          {/* Unit */}
          <div className="mb-4 pb-3 border-b">
            <div className="flex justify-between items-center mb-2">
              <label className="font-medium text-sm">Unidade</label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cor</label>
                <input
                  type="color"
                  value={layoutStyle.unit?.color || '#000000'}
                  onChange={(e) =>
                    handleTextStyleChange('unit', 'color', e.target.value)
                  }
                  className="w-full h-8"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tamanho (px)</label>
                <input
                  type="number"
                  min="0"
                  max="120"
                  value={layoutStyle.unit?.fontSize === 0 ? '' : (layoutStyle.unit?.fontSize || '')}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                    handleTextStyleChange('unit', 'fontSize', isNaN(value) ? 0 : value);
                  }}
                  className="w-full p-1 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Fonte</label>
                <select
                  value={layoutStyle.unit?.fontFamily || 'Arial, sans-serif'}
                  onChange={(e) =>
                    handleTextStyleChange('unit', 'fontFamily', e.target.value)
                  }
                  className="w-full p-1 border border-gray-300 rounded"
                >
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="'Courier New', monospace">Courier New</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                  <option value="'Segoe UI', sans-serif">Segoe UI</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Altura da Linha</label>
                <input
                  type="range"
                  min="0.8"
                  max="3"
                  step="0.1"
                  value={layoutStyle.unit?.lineHeight || 1.2}
                  onChange={(e) =>
                    handleTextStyleChange('unit', 'lineHeight', parseFloat(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.unit?.lineHeight || 1.2}
                </span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Espaçamento entre Letras (px)</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  value={layoutStyle.unit?.letterSpacing || 0}
                  onChange={(e) =>
                    handleTextStyleChange('unit', 'letterSpacing', parseFloat(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.unit?.letterSpacing || 0}px
                </span>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Espessura da Fonte</label>
                <input
                  type="range"
                  min="100"
                  max="900"
                  step="100"
                  value={layoutStyle.unit?.fontWeight || 400}
                  onChange={(e) =>
                    handleTextStyleChange('unit', 'fontWeight', parseInt(e.target.value))
                  }
                  className="w-full"
                />
                <span className="text-xs text-gray-500 block mt-1">
                  {layoutStyle.unit?.fontWeight || 400}
                </span>
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">Esticar Verticalmente (%)</label>
              <input
                type="range"
                min="100"
                max="300"
                step="10"
                value={layoutStyle.unit?.verticalStretch || 100}
                onChange={(e) =>
                  handleTextStyleChange('unit', 'verticalStretch', parseInt(e.target.value))
                }
                className="w-full"
              />
              <span className="text-xs text-gray-500 block mt-1">
                {layoutStyle.unit?.verticalStretch || 100}%
              </span>
            </div>

            {/* Vertical Alignment - Alinhamento vertical da unidade */}
            <div className="mt-4">
              <label className="block text-xs text-gray-500 mb-1">Alinhamento Vertical</label>
              <div style={{ width: 'calc(33.333% - 0.67rem)' }}>
                <select
                  value={layoutStyle.unit?.verticalAlignment || 'center'}
                  onChange={(e) =>
                    handleTextStyleChange('unit', 'verticalAlignment', e.target.value as 'center' | 'bottom')
                  }
                  className="w-full p-1 border border-gray-300 rounded"
                >
                  <option value="center">Centralizado</option>
                  <option value="bottom">Inferior</option>
                </select>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StyleEditor; 